import { useContext, useState } from 'react';
import { Backdrop } from '@spotify-internal/encore-web';

import {
  EmailType,
  emailTypeFromJSON,
  GetCodeRequest,
  GetCodeResponse,
  ValidateCodeRequest,
} from 'proto/generated/tintin_content_policy';
import { UserInfoContext } from 'context/UserInfo/UserInfoProvider';

import { TintinClient } from '../TintinClient';

import Step1EnterEmail from './Step1EnterEmail';
import Step2EnterCode from './Step2EnterCode';

enum Step {
  STEP1_ENTER_EMAIL,
  STEP2_ENTER_CODE,
}

export enum ErrorMessage {
  GENERIC = 'form_generic_error',
  VERIFICATION_CODE_INVALID = 'verification_code_invalid',
  EMAIL_INVALID = 'email_invalid',
  TOO_MANY_REPORTS_SUBMITTED = 'too_many_reports_submitted_by_user',
}

const onVerifyEmailButtonClick = async (
  email: string,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setEmailSent: React.Dispatch<React.SetStateAction<boolean>>,
  setCurrentStep: React.Dispatch<React.SetStateAction<Step>>,
  setResourceUri: React.Dispatch<React.SetStateAction<string>>,
  setEmailType: React.Dispatch<React.SetStateAction<EmailType>>,
  setUserProvidedEmail: (email: string) => void,
  setError: React.Dispatch<React.SetStateAction<string>>,
  client: TintinClient,
  timeout: number = 3000,
) => {
  setLoading(true);
  try {
    const request: GetCodeRequest = { email };
    const response: GetCodeResponse = await client.getVerificationCode(request);

    setLoading(false);
    setEmailSent(true);
    setError('');
    setResourceUri(response.resourceUri);
    setEmailType(emailTypeFromJSON(response.emailType));
    setTimeout(() => setCurrentStep(Step.STEP2_ENTER_CODE), timeout);

    setUserProvidedEmail(email);
  } catch (error: any) {
    if (error.message.includes('INVALID_EMAIL_ADDRESS')) {
      setError(ErrorMessage.EMAIL_INVALID);
    } else if (error.message.includes('TOO_MANY_REQUESTS')) {
      setError(ErrorMessage.TOO_MANY_REPORTS_SUBMITTED);
    } else {
      setError(ErrorMessage.GENERIC);
    }
  } finally {
    setLoading(false);
  }
};

const onVerifyCodeButtonClick = async (
  email: string,
  resourceUri: string,
  code: string,
  emailType: EmailType,
  onSuccessfulFinishWithEmail: (email: string, resourceUri: string, code: string, emailType: EmailType) => void,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setIdentityVerified: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string>>,
  client: TintinClient,
  timeout: number = 3000,
) => {
  setLoading(true);
  try {
    const request: ValidateCodeRequest = { email, resourceUri, code };
    const codeValid = await client.validateVerificationCode(request);

    if (codeValid) {
      setLoading(false);
      const f = () => onSuccessfulFinishWithEmail(email, resourceUri, code, emailType);
      setIdentityVerified(true);
      setError('');
      setTimeout(f, timeout);
    } else {
      setError(ErrorMessage.VERIFICATION_CODE_INVALID);
    }
  } catch {
    setError(ErrorMessage.GENERIC);
  } finally {
    setLoading(false);
  }
};

const EmailVerificationDialog = ({
  client,
  onCancel,
  onSuccessfulFinish,
  timeout = 3000,
  userAccountEmail,
}: {
  client: TintinClient;
  onCancel: () => void;
  onSuccessfulFinish: (email: string, resourceUri: string, code: string, emailType: EmailType) => void;
  timeout?: number;
  userAccountEmail?: string;
}) => {
  const [email, setEmail] = useState<string>(userAccountEmail ? userAccountEmail : '');
  const [emailValid, setEmailValid] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [code, setCode] = useState<string>('');
  const [codeValid, setCodeValid] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<Step>(Step.STEP1_ENTER_EMAIL);
  const [identityVerified, setIdentityVerified] = useState<boolean>(false);
  const [resourceUri, setResourceUri] = useState<string>('');
  const [emailType, setEmailType] = useState<EmailType>(EmailType.EMAIL_TYPE_EXTERNAL);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const { setUserProvidedEmail } = useContext(UserInfoContext);

  const stepComponents: { [key in Step]?: JSX.Element } = {
    [Step.STEP1_ENTER_EMAIL as number]: (
      <Step1EnterEmail
        email={email}
        emailValid={emailValid}
        emailSent={emailSent}
        isLoggedInWithEmail={!!(userAccountEmail && userAccountEmail.length > 0)}
        loading={loading}
        setEmail={setEmail}
        setEmailValid={setEmailValid}
        error={error}
        onCancel={onCancel}
        onClick={() =>
          onVerifyEmailButtonClick(
            email,
            setLoading,
            setEmailSent,
            setCurrentStep,
            setResourceUri,
            setEmailType,
            setUserProvidedEmail,
            setError,
            client,
            timeout,
          )
        }
      />
    ),
    [Step.STEP2_ENTER_CODE as number]: (
      <Step2EnterCode
        code={code}
        codeValid={codeValid}
        loading={loading}
        setCode={setCode}
        setCodeValid={setCodeValid}
        identityVerified={identityVerified}
        error={error}
        onCancel={onCancel}
        onClick={() =>
          onVerifyCodeButtonClick(
            email,
            resourceUri,
            code,
            emailType,
            onSuccessfulFinish,
            setLoading,
            setIdentityVerified,
            setError,
            client,
            timeout,
          )
        }
      />
    ),
  };

  return (
    <Backdrop center style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
      {stepComponents[currentStep]}
    </Backdrop>
  );
};

export default EmailVerificationDialog;
